import {useLazyQuery, useQuery} from '@apollo/client';
import {Link, useParams} from 'react-router-dom';
import {Alert, Button, Radio} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {toast} from 'react-toastify';
import {Section} from 'pages/home/shared/DashboardUI';
import {
  EXPORT_SUPPORT_AGENT_TICKETS,
  GET_SUPPORT_AGENT_PERFORMANCE,
} from './resolvers';
import {usePerformanceFilters} from './usePerformanceFilters';
import {PerformanceFilterBar} from './PerformanceFilterBar';
import {MetricCards} from './MetricCards';
import {RankingTable} from './RankingTable';
import {TrendCharts} from './TrendCharts';
import {TicketsSection} from './TicketsSection';
import {Benchmarks} from './Benchmarks';
import {TargetLink} from './TargetLink';
import {
  PERFORMANCE_PATH,
  UNATTRIBUTED,
  agentName,
  describeRange,
  detailInput,
  downloadCsv,
  exportInput,
  writeFilters,
} from './performanceUtils';
import {Explained, TERMS} from './Info';
import styles from './performance.module.scss';

export const BucketToggle = ({value, update}) => (
  <Radio.Group
    size="small"
    value={value}
    onChange={event => update({bucket: event.target.value})}
    optionType="button"
    options={[
      {value: 'DAY', label: 'Daily'},
      {value: 'WEEK', label: 'Weekly'},
    ]}
  />
);

export const TRENDS_TERM =
  'Each point is one UTC day or week. A gap in the rating or time line means nothing was rated or measured then — it is not a zero.';

/**
 * One agent's report — or Unattributed's — for the filters the overview had.
 * The ticket list covers tickets opened, resolved or abandoned in the period
 * plus live pending ones, so its total is not the resolved count.
 */
export const AgentPerformanceDetail = () => {
  const {agentId: rawAgentId} = useParams();
  const agentId = decodeURIComponent(rawAgentId || '');
  const {filters, update} = usePerformanceFilters();
  const {data, loading, error, refetch} = useQuery(
    GET_SUPPORT_AGENT_PERFORMANCE,
    {
      variables: {input: detailInput(filters, agentId)},
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !agentId,
    },
  );
  // Asked for on click only, never cached: it carries customer feedback.
  const [runExport, exportState] = useLazyQuery(EXPORT_SUPPORT_AGENT_TICKETS, {
    fetchPolicy: 'no-cache',
  });

  const result = data?.getSupportAgentPerformance;
  const report =
    result?.__typename === 'ResponseWithSupportAgentPerformance'
      ? result.data
      : null;
  const errorMessage =
    result?.__typename === 'Error'
      ? result.message
      : error && !result
      ? "Couldn't load this agent's performance."
      : null;
  const isUnattributed = agentId === UNATTRIBUTED;
  const title = isUnattributed
    ? 'Unattributed'
    : agentName(report?.agent, agentId);

  const exportCsv = async () => {
    try {
      const response = await runExport({
        variables: {input: exportInput(filters, agentId)},
      });
      const node = response.data?.exportSupportAgentTickets;
      if (node?.__typename === 'Error') {
        toast.error(node.message);
        return;
      }
      if (!node?.data) {
        toast.error("Couldn't build the CSV. Try again.");
        return;
      }
      downloadCsv(node.data);
      toast.success(
        `Downloaded ${node.data.rowCount} ticket${
          node.data.rowCount === 1 ? '' : 's'
        }`,
      );
    } catch (err) {
      toast.error(err.message || "Couldn't build the CSV. Try again.");
    }
  };

  return (
    <div className={styles.page}>
      <Link
        className={styles.backLink}
        to={`${PERFORMANCE_PATH}${writeFilters({
          ...filters,
          page: 1,
          bucket: 'DAY',
          tickets: null,
          sort: null,
        })}`}>
        <ArrowLeftOutlined /> All agents
      </Link>
      <div className={styles.detailHead}>
        <span className={styles.eyebrow}>
          {isUnattributed ? 'Group performance' : 'Agent performance'}
        </span>
        <h2 className={styles.detailTitle}>{title}</h2>
        <p className={styles.detailNote}>
          {isUnattributed
            ? 'Resolved tickets with no credited agent — closed by Sassandra (AI), an FAQ article or the customer — and pending tickets nobody is assigned to.'
            : `${title}'s ratings, speed and tickets.`}{' '}
          To compare everyone, go back to <em>All agents</em>.
        </p>
      </div>

      <PerformanceFilterBar filters={filters} update={update} />
      <div className={styles.scopeRow}>
        <p className={styles.scope}>
          {describeRange(filters.from, filters.to)} · Pending now is live
        </p>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading && Boolean(report)}
          onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}

      <MetricCards
        metrics={report?.metrics}
        firstResponse={report?.firstResponse ?? null}
        pendingAsOf={report?.pendingAsOf}
        pendingTerm={isUnattributed ? TERMS.pending : TERMS.agentPending}
        loading={loading && !report}
        targetAction={<TargetLink />}
      />

      {!isUnattributed && report?.benchmarks && (
        <Section
          title="Against the team"
          description="Same dates and filters. Team figures are worked out by the server across every support agent.">
          <Benchmarks metrics={report.metrics} benchmarks={report.benchmarks} />
        </Section>
      )}

      <Section
        title={<Explained text={TRENDS_TERM}>Trends</Explained>}
        actions={<BucketToggle value={filters.bucket} update={update} />}>
        <TrendCharts
          trends={report?.trends}
          bucket={filters.bucket}
          loading={loading}
          metrics={report?.metrics}
        />
      </Section>

      <div className={styles.twoColumns}>
        <Section title="Top categories handled">
          <RankingTable
            rows={report?.categories}
            basis="RESOLVED"
            loading={loading}
          />
        </Section>
        <Section title="Top issues handled">
          <RankingTable
            rows={report?.issues}
            basis="RESOLVED"
            loading={loading}
            showAncestry
          />
        </Section>
      </div>

      <TicketsSection
        filters={filters}
        update={update}
        tickets={report?.tickets}
        pagination={report?.pagination}
        loading={loading}
        actions={
          <Button
            size="small"
            icon={<DownloadOutlined />}
            loading={exportState.loading}
            onClick={exportCsv}>
            Download CSV
          </Button>
        }
      />
    </div>
  );
};
